/**
 * Resumen de cómo van los ítems de la operación, SOLO LECTURA (Diego 2026-10-05): una fila por ítem con la
 * subpartida y la cantidad declarada, pesos, modalidad y país, el registro asignado con su reserva, el beneficio
 * de origen con el código del acuerdo que va en la declaración, y qué le falta. Se abre en ventana independiente
 * desde la pantalla del DO. No modifica datos; exporta a Excel (CSV) para revisarlo con el cliente.
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-10-05
 */
import React, { useMemo, useState } from "react";
import { Button } from "codekit";
import { useGetResumenItemsQuery } from "../../../../../libs/redux/slices/Modimpxx/Documexx/Itemdoxx/itemdoxxApiSlice";
import "../../../../Tablerox/riodo.css";
import "./resuitem.css";

const numeroCo = (valor, decimales = 2) => {
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero.toLocaleString("es-CO", { minimumFractionDigits: 0, maximumFractionDigits: decimales }) : "—";
};

const ETIQUETA_REGISTRO = {
  ASIGNADO: { tono: "verde", texto: "Asignado" },
  SIN_REGISTRO: { tono: "coral", texto: "Sin registro" },
  NO_REQUIERE: { tono: "gris", texto: "No requiere" },
};

const COLUMNAS_CSV = [
  "Factura", "Item", "Producto", "Descripcion", "Subpartida", "Unidad subpartida", "Cantidad factura", "Unidad factura", "Factor",
  "Cantidad declarada", "Peso neto", "Peso bruto", "Valor", "Modalidad", "Pais de origen", "Estado mercancia", "Tipo declaracion",
  "Paquete DAV", "Registro", "Numero registro", "Vigencia", "Reservado", "Motivo sin registro", "Beneficio de origen", "Acuerdo",
  "Prueba de origen cargada", "Seriales", "Condiciones", "Que le falta",
];

const filaCsv = (it) => [
  it.FACIDXXX, it.SECITEXX, it.PROIDXXX, it.DESCRIPCION, it.SUBPARTIDA.CODIGO, it.SUBPARTIDA.UNIDAD, it.CANTIDAD.FACTURA,
  it.CANTIDAD.UNIDAD_FACTURA, it.CANTIDAD.FACTOR || "", it.CANTIDAD.DECLARADA ?? "", it.PESO.NETO, it.PESO.BRUTO, it.VALOR, it.MODALIDAD,
  it.PAIS_ORIGEN, it.ESTADO_MERCANCIA, it.TIPO_DECLARACION, it.PAQUETE_DAV, ETIQUETA_REGISTRO[it.REGISTRO.ESTADO]?.texto ?? it.REGISTRO.ESTADO,
  it.REGISTRO.NUMERO, it.REGISTRO.VIGENCIA, it.REGISTRO.RESERVADO ?? "", it.REGISTRO.MOTIVO, it.ORIGEN.APLICA, it.ORIGEN.ACUERDO,
  it.ORIGEN.PRUEBA_CARGADA, it.SERIALES + (it.SERIALES_DETALLE ? ` (${it.SERIALES_DETALLE})` : ""), it.CONDICIONES.join(" / "), it.FALTA.join(" / "),
];

const Resuitem = ({ DOCNROXX, onVolver }) => {
  const { data, isFetching, isError } = useGetResumenItemsQuery(DOCNROXX, { skip: !DOCNROXX });
  const resumen = data?.data ?? null;
  const [factura, setFactura] = useState("");
  const [soloFaltan, setSoloFaltan] = useState(false);
  const [buscar, setBuscar] = useState("");

  const items = useMemo(() => {
    const texto = buscar.trim().toUpperCase();
    return (resumen?.ITEMS ?? []).filter((it) =>
      (factura === "" || it.FACIDXXX === factura) &&
      (!soloFaltan || it.FALTA.length > 0) &&
      (texto === "" || `${it.PROIDXXX} ${it.DESCRIPCION} ${it.SUBPARTIDA.CODIGO} ${it.REGISTRO.NUMERO}`.toUpperCase().includes(texto))
    );
  }, [resumen, factura, soloFaltan, buscar]);

  //Excel (CSV con punto y coma, acentos intactos): el valor numérico va con coma decimal, como en toda CIMA
  const handleExportar = () => {
    const celda = (v) => `"${(typeof v === "number" ? String(v).replace(".", ",") : String(v ?? "")).replace(/"/g, '""')}"`;
    const lineas = [COLUMNAS_CSV.join(";"), ...items.map((it) => filaCsv(it).map(celda).join(";"))];
    const blob = new Blob(["﻿" + lineas.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const enlace = document.createElement("a");
    enlace.href = URL.createObjectURL(blob);
    enlace.download = `resumen-items-DO-${DOCNROXX}.csv`;
    enlace.click();
    URL.revokeObjectURL(enlace.href);
  };

  const conteo = resumen?.CONTEO ?? {};
  let facturaActual = "";

  return (
    <div className="riodo resui-lienzo">
      <div className="rio-band">
        <div>
          <div className="rio-band-sub">Módulo de Importación › Resumen de ítems · solo lectura</div>
          <div className="rio-band-title">DO {DOCNROXX}{resumen?.OPERACION?.CLINOMXX ? ` · ${resumen.OPERACION.CLINOMXX}` : ""}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button type="button" variant="secondary-bold" size="m" onClick={handleExportar} disabled={items.length === 0}>Exportar a Excel</Button>
          {onVolver && <Button type="button" variant="secondary-bold" size="m" onClick={onVolver}>Cerrar</Button>}
        </div>
      </div>

      <div className="resui-strip">
        {[["Ítems", conteo.ITEMS], ["Completos", conteo.COMPLETOS], ["Con registro asignado", conteo.CON_REGISTRO], ["Con beneficio de origen", conteo.CON_ORIGEN]].map(([t, v]) => (
          <div className="rio-strip-card rio-card" key={t} style={{ padding: "10px 14px" }}>
            <div className="rio-kicker">{t}</div>
            <div className="rio-strip-valor">{v ?? "—"}</div>
          </div>
        ))}
      </div>

      <div className="resui-filtros">
        <select value={factura} onChange={(e) => setFactura(e.target.value)}>
          <option value="">Todas las facturas ({(resumen?.FACTURAS ?? []).length})</option>
          {(resumen?.FACTURAS ?? []).map((f) => <option key={f} value={f}>Factura {f}</option>)}
        </select>
        <input placeholder="Buscar por código, descripción, subpartida o registro" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
          <input type="checkbox" checked={soloFaltan} onChange={(e) => setSoloFaltan(e.target.checked)} style={{ minWidth: 0, height: "auto" }} /> Solo los que tienen algo pendiente
        </label>
        <span className="resui-sub">{items.length} ítem(s)</span>
      </div>

      <div className="rio-card resui-tabla-wrap">
        {isFetching && <div className="resui-vacio">Cargando…</div>}
        {isError && <div className="resui-vacio">No fue posible leer el resumen de la operación.</div>}
        {!isFetching && !isError && items.length === 0 && <div className="resui-vacio">No hay ítems que mostrar.</div>}
        {!isFetching && items.length > 0 && (
          <table className="resui-tabla">
            <thead>
              <tr>
                <th>Ítem · producto</th><th>Subpartida</th><th className="num">Cantidad factura</th><th className="num">Cantidad declarada</th>
                <th className="num">Peso neto / bruto</th><th className="num">Valor</th><th>Modalidad · país</th><th>Registro de importación</th>
                <th>Beneficio de origen</th><th>Seriales</th><th>Declaración</th><th>Qué le falta</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const filaFactura = it.FACIDXXX !== facturaActual;
                facturaActual = it.FACIDXXX;
                const reg = ETIQUETA_REGISTRO[it.REGISTRO.ESTADO] ?? { tono: "gris", texto: it.REGISTRO.ESTADO };
                return (
                  <React.Fragment key={`${it.FACIDXXX}-${it.SECITEXX}`}>
                    {filaFactura && <tr className="resui-fac"><td colSpan={12}>Factura {it.FACIDXXX}</td></tr>}
                    <tr>
                      <td><div className="cod">{it.SECITEXX} · {it.PROIDXXX}</div><div className="resui-sub">{it.DESCRIPCION}</div></td>
                      <td><div className="cod">{it.SUBPARTIDA.CODIGO || "—"}</div><div className="resui-sub">{it.SUBPARTIDA.UNIDAD}</div></td>
                      <td className="num">{numeroCo(it.CANTIDAD.FACTURA, 5)} {it.CANTIDAD.UNIDAD_FACTURA}</td>
                      <td className="num">
                        {it.CANTIDAD.DECLARADA === null ? <span className="resui-chip ambar">Sin factor</span> : `${numeroCo(it.CANTIDAD.DECLARADA, 5)} ${it.SUBPARTIDA.UNIDAD}`}
                        {it.CANTIDAD.FACTOR > 0 && <div className="resui-sub">factor {numeroCo(it.CANTIDAD.FACTOR, 5)}</div>}
                      </td>
                      <td className="num">{numeroCo(it.PESO.NETO, 5)} / {numeroCo(it.PESO.BRUTO, 5)} kg</td>
                      <td className="num">USD {numeroCo(it.VALOR, 2)}</td>
                      <td><div>{it.MODALIDAD || "—"}</div><div className="resui-sub">{it.PAIS_ORIGEN || "—"}</div></td>
                      <td>
                        <span className={`resui-chip ${reg.tono}`}>{reg.texto}</span>
                        {it.REGISTRO.NUMERO && <div className="cod">{it.REGISTRO.NUMERO}</div>}
                        {it.REGISTRO.ESTADO === "ASIGNADO" && (
                          <div className="resui-sub">{it.REGISTRO.TIPO} · vence {it.REGISTRO.VIGENCIA || "—"} · reservado {numeroCo(it.REGISTRO.RESERVADO, 5)}</div>
                        )}
                        {it.REGISTRO.MOTIVO && <div className="resui-sub">{it.REGISTRO.MOTIVO}</div>}
                      </td>
                      <td>
                        <span className={`resui-chip ${it.ORIGEN.APLICA === "SI" ? "verde" : "gris"}`}>{it.ORIGEN.APLICA === "SI" ? "Aplica" : "No aplica"}</span>
                        {it.ORIGEN.APLICA === "SI" && (
                          <div className="resui-sub">
                            {it.ORIGEN.ACUERDO ? `Acuerdo ${it.ORIGEN.ACUERDO}` : "Sin código de acuerdo asignado"}
                            {it.ORIGEN.GRUPO ? ` · ${it.ORIGEN.GRUPO}` : ""} · prueba {it.ORIGEN.PRUEBA_CARGADA === "SI" ? "cargada" : "sin cargar"}
                          </div>
                        )}
                        {it.ORIGEN.EXCLUIDO === "SI" && <div className="resui-sub">Excluido del beneficio</div>}
                      </td>
                      <td>
                        <span className={`resui-chip ${it.SERIALES === "SI" ? "verde" : it.SERIALES === "NO" ? "coral" : "gris"}`}>{it.SERIALES}</span>
                        {it.SERIALES_DETALLE && <div className="resui-sub">{it.SERIALES_DETALLE}</div>}
                      </td>
                      <td>
                        <div>{it.TIPO_DECLARACION || "—"}</div>
                        <div className="resui-sub">{it.ESTADO_MERCANCIA}{it.PAQUETE_DAV ? ` · DAV ${it.PAQUETE_DAV}` : ""}</div>
                        {it.CONDICIONES.map((c) => <span key={c} className="resui-chip azul" style={{ marginRight: 4 }}>{c}</span>)}
                      </td>
                      <td>
                        {it.FALTA.length === 0
                          ? <span className="resui-chip verde">Completo</span>
                          : <div className="resui-falta">{it.FALTA.map((f) => <span key={f} className="resui-chip coral">{f}</span>)}</div>}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Resuitem;
